import _ from 'lodash';
import React, {FunctionComponent, useRef, useState, ChangeEvent, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {TextareaAutosize, Grid, makeStyles, IconButton, Popover} from '@material-ui/core';
import {ZoomInSharp} from '@material-ui/icons';
import Checkbox from '@treez-ui/core/forms/Checkbox';
import {CloseIcon, EditIcon} from '@treez-ui/core/icons';
import {ProductField} from '@treez/commons/sharedTypings/product';
import useDebounce from '~/lib/useDebounce';
import {updateMergeProduct} from '~/redux/actions/MergeActions';
import {selectMergeProductDescription} from '~/redux/selectors/mergeSelectors';
import {treezColorMap, TreezColor} from '~/styles/constants';

const {DESCRIPTIONS} = ProductField;

const {OFF_WHITE, GREY_DARK} = TreezColor;

interface Props {
    text: string;
    maxCharOnPreview?: number;
    clickOrEdit: 'click' | 'edit';
}

export const MergeDescriptionCell: FunctionComponent<Props> = (props: Props) => {
    const {clickOrEdit, text, maxCharOnPreview = 800} = props;

    const valueToCompare = useSelector(selectMergeProductDescription, _.isEqual);

    const isSelected = _.isEqual(text, valueToCompare);

    const dispatch = useDispatch();

    const [editedText, setEditedText] = useState<string>(text);
    const [examineFieldOpen, setExamineFieldOpen] = useState<boolean>(false);
    const [editFieldOpen, setEditFieldOpen] = useState<boolean>(false);

    const debouncedEditedText = useDebounce(editedText, 500);

    useEffect(() => {
        dispatch(updateMergeProduct(DESCRIPTIONS, debouncedEditedText));
    }, [debouncedEditedText]);

    useEffect(() => {
        setEditedText(text);
    }, [text]);

    const handleClick = () => {
        dispatch(updateMergeProduct(DESCRIPTIONS, text));
    };

    const handleEdit = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const {
            target: {value},
        } = event;

        setEditedText(value);
    };

    const useStyles = makeStyles({
        fieldContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            height: '400px',
            padding: '8px',
            borderLeft: clickOrEdit === 'click' ? '1px solid grey' : '',
        },
        textArea: {
            maxWidth: '100%',
            minWidth: '100%',
            minHeight: '100px',
            padding: '8px',
            boxSizing: 'border-box',
        },
        textContent: {
            cursor: 'pointer',
            height: '80%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
        },
        optionsContainer: {
            width: '100%',
            height: '30px',
            display: 'flex',
            justifyContent: 'space-between',
        },
        popOver: {
            width: '400px',
            height: 'auto',
        },
        popOverTextContainer: {
            padding: '8px',
        },
        popOverHeader: {
            display: 'flex',
            height: '30px',
            alignItems: 'center',
            backgroundColor: treezColorMap[OFF_WHITE],
            borderBottom: `1px solid ${treezColorMap[GREY_DARK]}`,
        },
    });

    const {
        fieldContainer,
        optionsContainer,
        popOver,
        popOverHeader,
        popOverTextContainer,
        textArea,
        textContent,
    } = useStyles();

    const cellRef = useRef<HTMLDivElement>(null);

    return (
        <Grid item className={fieldContainer} ref={cellRef}>
            <Popover anchorEl={cellRef.current} open={examineFieldOpen}>
                <Grid className={popOver}>
                    <Grid className={popOverHeader}>
                        <IconButton onClick={() => setExamineFieldOpen(false)}>
                            <CloseIcon color={treezColorMap[GREY_DARK]} />
                        </IconButton>
                    </Grid>
                    <Grid className={popOverTextContainer} onClick={handleClick}>
                        {text}
                    </Grid>
                </Grid>
            </Popover>
            <Popover anchorEl={cellRef.current} open={editFieldOpen}>
                <Grid className={popOver}>
                    <Grid className={popOverHeader}>
                        <IconButton
                            onClick={() => {
                                setEditFieldOpen(false);
                            }}
                        >
                            <CloseIcon color={treezColorMap[GREY_DARK]} />
                        </IconButton>
                    </Grid>
                    <Grid className={popOverTextContainer} onClick={handleClick}>
                        <TextareaAutosize
                            className={textArea}
                            onChange={handleEdit}
                            placeholder="Product Description"
                            value={editedText}
                        />
                    </Grid>
                </Grid>
            </Popover>
            <Grid className={textContent} onClick={handleClick}>
                {_.truncate(text, {
                    length: maxCharOnPreview,
                })}
            </Grid>
            <Grid className={optionsContainer}>
                <IconButton onClick={() => setExamineFieldOpen(true)}>
                    <ZoomInSharp />
                </IconButton>
                {clickOrEdit === 'click' && (
                    <Checkbox label="" checked={isSelected} onClick={() => handleClick()} />
                )}
                {clickOrEdit === 'edit' && (
                    <IconButton onClick={() => setEditFieldOpen(true)}>
                        <EditIcon />
                    </IconButton>
                )}
            </Grid>
        </Grid>
    );
};

export default React.memo(MergeDescriptionCell);
